import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, X, Volume2, VolumeX, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const VoiceCommandButton = ({ token, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    return localStorage.getItem('voice_response_enabled') !== 'false';
  });
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const streamRef = useRef(null);
  const recordingStartRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('voice_response_enabled', voiceEnabled);
  }, [voiceEnabled]);

  const startRecording = async () => {
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setResponse('Microphone is not supported in this browser. Please use Chrome or Safari.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Try webm first, fall back to mp4/other formats for iOS
      let options = {};
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        options = { mimeType: 'audio/webm' };
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options = { mimeType: 'audio/mp4' };
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;

        // Reject recordings that are too short (< 0.5s) or empty.
        // This catches accidental quick taps and silent recordings.
        const duration = recordingStartRef.current ? (Date.now() - recordingStartRef.current) : 0;
        if (audioBlob.size < 1000 || duration < 500) {
          setResponse('Recording too short. Hold the button and speak for at least 1 second, then release.');
          return;
        }
        await processAudio(audioBlob);
      };

      // Use timeslice so iOS WKWebView streams data continuously instead of buffering.
      mediaRecorder.start(250);
      recordingStartRef.current = Date.now();
      setIsRecording(true);
      setTranscript('');
      setResponse('');
    } catch (err) {
      console.error('Microphone access denied:', err);
      const isNative = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        if (isNative) {
          setResponse('Microphone access was denied. Please go to your device Settings > Apps > NutriFitness > Permissions and enable Microphone, then restart the app.');
        } else {
          setResponse('Microphone access was denied. Tap the lock icon in your browser address bar → Site settings → Microphone → Allow, then reload the page.');
        }
      } else if (err.name === 'NotFoundError') {
        setResponse('No microphone found on this device.');
      } else {
        setResponse('Could not access microphone. Please check your device settings.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      const ext = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
      formData.append('audio', audioBlob, `recording.${ext}`);
      formData.append('voice_response', voiceEnabled ? 'true' : 'false');

      const res = await axios.post(`${API}/api/voice/command`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      });

      const { transcript: text, response: reply, action, audio } = res.data;
      setTranscript(text);
      setResponse(reply);

      // Play audio response
      if (audio && voiceEnabled) {
        const audioSrc = `data:audio/mp3;base64,${audio}`;
        if (audioPlayerRef.current) {
          audioPlayerRef.current.src = audioSrc;
          audioPlayerRef.current.play().catch(() => {});
        }
      }

      // Handle navigation action
      if (action?.navigate && onNavigate) {
        setTimeout(() => {
          onNavigate(action.navigate);
          setIsOpen(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Voice command error:', err);
      setResponse('Sorry, I had trouble processing that. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isRecording) stopRecording();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }
    setIsOpen(false);
    setTranscript('');
    setResponse('');
  };

  if (!isOpen) {
    return (
      <button
        data-testid="voice-command-btn"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-5 z-50 w-14 h-14 bg-gradient-to-r from-[#FF6600] to-[#FF8533] rounded-full shadow-lg shadow-orange-500/30 flex items-center justify-center text-white hover:scale-110 active:scale-95 transition-transform"
      >
        <Mic size={24} />
      </button>
    );
  }

  return (
    <>
      <audio ref={audioPlayerRef} className="hidden" />
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end justify-center" onClick={handleClose}>
        <div
          className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-t-3xl p-6 pb-10 space-y-5 animate-slide-up"
          onClick={(e) => e.stopPropagation()}
          data-testid="voice-command-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Voice Assistant</h3>
            <div className="flex items-center gap-3">
              <button
                data-testid="voice-toggle-btn"
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title={voiceEnabled ? 'Mute voice' : 'Enable voice'}
              >
                {voiceEnabled
                  ? <Volume2 size={18} className="text-[#FF6600]" />
                  : <VolumeX size={18} className="text-gray-400" />
                }
              </button>
              <button onClick={handleClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <X size={18} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Transcript */}
          {transcript && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3" data-testid="voice-transcript">
              <p className="text-xs text-gray-400 mb-1">You said:</p>
              <p className="text-sm text-gray-900 dark:text-white">{transcript}</p>
            </div>
          )}

          {/* Response */}
          {response && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-3" data-testid="voice-response">
              <p className="text-sm text-gray-900 dark:text-white leading-relaxed">{response}</p>
            </div>
          )}

          {/* Processing indicator */}
          {isProcessing && (
            <div className="flex items-center gap-3 justify-center py-4" data-testid="voice-processing">
              <Loader2 size={20} className="text-[#FF6600] animate-spin" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Processing your command...</span>
            </div>
          )}

          {/* Recording button */}
          <div className="flex flex-col items-center gap-3">
            {!isProcessing && (
              <>
                <button
                  data-testid="voice-record-btn"
                  onMouseDown={(e) => { if (!('ontouchstart' in window)) startRecording(); }}
                  onMouseUp={(e) => { if (!('ontouchstart' in window)) stopRecording(); }}
                  onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
                  onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                    isRecording
                      ? 'bg-red-500 scale-110 animate-pulse shadow-lg shadow-red-500/40'
                      : 'bg-gradient-to-r from-[#FF6600] to-[#FF8533] hover:scale-105 shadow-lg shadow-orange-500/30'
                  }`}
                >
                  {isRecording
                    ? <MicOff size={32} className="text-white" />
                    : <Mic size={32} className="text-white" />
                  }
                </button>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {isRecording ? 'Release to send' : 'Hold to speak'}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VoiceCommandButton;
